/**
 * "Test stream" (ENG-2470): open one camera's stream from our side — over
 * the connector's real network path, the address the customer allowlists or
 * their tunnel — and show what we saw: a snapshot, the video's format, how
 * we got there, and on failure the same finding card as the camera check.
 */
import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { TsButton } from "../../Troubleshooting/TroubleshootingUi";
import { FindingCard } from "../../Troubleshooting/tabs/DiagnosisTab";
import { Finding } from "../../Troubleshooting/troubleshootingInterfaces";
import { StepLadder } from "../SiteConnectivity/SiteConnectivityPanel";
import {
  OWNER_MAP,
  formatClock,
  stepLabel,
  videoFacts,
} from "../SiteConnectivity/connectivityModel";
import { CameraClip } from "../SiteConnectivity/CameraMediaView";
import {
  STREAM_TEST_OFF_TEXT,
  STREAM_TEST_SITE_TYPE_TEXT,
  networkPathText,
  refusalText,
} from "./streamTestApi";
import { StreamTest, StreamTestResultDetail } from "./streamTestTypes";
import { StreamTestNotice, useStreamTest } from "./useStreamTest";

/** "0:42" */
export const formatElapsed = (ms: number): string => {
  const total = Math.floor(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

/** "H.264 · 1920×1080 · 15 fps · first frame in 1.2 s", or null. */
export const streamFacts = (result: StreamTestResultDetail): string | null =>
  videoFacts(result);

/** admin's finding for a failed test, as the Troubleshooter's card. */
export const streamTestFinding = (test: StreamTest): Finding | null => {
  const result = test.result;
  const finding = result?.finding;
  if (!result || !finding) return null;
  const failedStep = result.steps?.find((step) => step.state === "fail");
  return {
    id: `stream-test-${finding.code}`,
    severity: "bad",
    owner: OWNER_MAP[finding.owner] ?? "shared",
    kind: "stream",
    title: finding.title,
    since: null,
    body: finding.body,
    steps: finding.steps ?? [],
    affectedSiteIds: [test.site_id],
    metric: {
      value: "Stream",
      label: failedStep
        ? `stops at “${stepLabel(failedStep.id)}”`
        : "didn’t open",
    },
    actions: [],
    siteId: test.site_id,
    probeCameraIds: [test.camera_id],
    detailLine: result.detail ?? undefined,
  };
};

const copyToClipboard = async (value: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
};

const Spinner = ({ text }: { text: string }) => (
  <div
    className="flex items-center gap-3 py-3 font-mulish text-[14px] text-[#4c5257]"
    role="status"
    data-testid="stream-test-progress"
  >
    <CircularProgress size={18} sx={{ color: "#FF8400" }} />
    {text}
  </div>
);

const NoticeBox = ({
  notice,
  onShowRun,
  onDismiss,
}: {
  notice: StreamTestNotice;
  onShowRun: (runId: string) => void;
  onDismiss: () => void;
}) => (
  <div
    className="flex items-start justify-between gap-3 rounded border border-[#b25e00] bg-[#fff4e5] px-3 py-2 font-mulish text-[14px] text-actuate-blue"
    role="alert"
    data-testid="stream-test-notice"
  >
    <span>
      {notice.kind === "refused" ? refusalText(notice) : notice.message}
    </span>
    <span className="flex shrink-0 gap-3">
      {notice.kind === "refused" && notice.runId && (
        <button
          type="button"
          className="text-[13px] font-bold text-[#283e6d]"
          onClick={() => onShowRun(notice.runId as string)}
          data-testid="stream-test-show-run"
        >
          {notice.code === "test_active" ? "Show that test" : "Show its result"}
        </button>
      )}
      <button
        type="button"
        className="text-[13px] font-bold text-[#283e6d]"
        onClick={onDismiss}
      >
        Dismiss
      </button>
    </span>
  </div>
);

/** Copy exactly what's shown: admin has already masked the password. */
const MaskedUrl = ({ url }: { url: string }) => {
  const [copied, setCopied] = React.useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <code
        className="min-w-0 break-all rounded bg-[#f3f4f6] px-2 py-1 font-mono text-[12px] text-actuate-blue"
        data-testid="stream-test-url"
      >
        {url}
      </code>
      <TsButton
        variant="quiet"
        size="sm"
        onClick={async () => setCopied(await copyToClipboard(url))}
        data-testid="stream-test-copy-url"
      >
        {copied ? "Copied" : "Copy"}
      </TsButton>
    </div>
  );
};

const Snapshot = ({
  url,
  expiresAt,
  onRunAgain,
  canRunAgain,
}: {
  url: string;
  expiresAt: string | null;
  onRunAgain: () => void;
  canRunAgain: boolean;
}) => {
  const expiry = expiresAt ? Date.parse(expiresAt) : NaN;
  const [expired, setExpired] = React.useState(
    !Number.isNaN(expiry) && expiry <= Date.now()
  );
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => {
    setBroken(false);
    if (Number.isNaN(expiry)) {
      setExpired(false);
      return;
    }
    const left = expiry - Date.now();
    if (left <= 0) {
      setExpired(true);
      return;
    }
    setExpired(false);
    const timer = setTimeout(() => setExpired(true), left);
    return () => clearTimeout(timer);
  }, [url, expiry]);

  if (expired || broken) {
    return (
      <div
        className="flex flex-wrap items-center gap-3 rounded border border-dashed border-[#d1d5db] bg-[#f9fafb] px-4 py-6 font-mulish text-[14px] text-[#4c5257]"
        data-testid="stream-test-snapshot-expired"
      >
        The picture from this test has expired.
        {canRunAgain && (
          <TsButton size="sm" onClick={onRunAgain}>
            Run again
          </TsButton>
        )}
      </div>
    );
  }
  return (
    <img
      src={url}
      alt="What we saw from the camera's stream"
      className="block max-h-[360px] max-w-full rounded border border-[#d1d5db] bg-black object-contain"
      onError={() => setBroken(true)}
      data-testid="stream-test-snapshot"
    />
  );
};

const ResultCard = ({
  test,
  fromLatest,
  onRunAgain,
  canRunAgain,
}: {
  test: StreamTest;
  fromLatest: boolean;
  onRunAgain: () => void;
  canRunAgain: boolean;
}) => {
  const result = test.result;
  const when = test.checked_at ?? test.created_at;
  const path = networkPathText(test.network_path);
  const finding = streamTestFinding(test);
  const facts = result ? streamFacts(result) : null;
  return (
    <div
      className="flex flex-col gap-3 rounded-md border border-[#d1d5db] bg-white p-4"
      data-testid="stream-test-result"
      data-ok={result?.ok ? "true" : "false"}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p
          className={`m-0 font-mulish text-[16px] font-extrabold ${
            result?.ok ? "text-[#1e7b34]" : "text-[#b3261e]"
          }`}
        >
          {result?.ok
            ? "We got video from this camera"
            : result
            ? "We couldn't get video from this camera"
            : "The stream test couldn't finish"}
        </p>
        {when && (
          <span
            className="font-mulish text-[13px] text-[#6b7075]"
            data-testid="stream-test-when"
          >
            {fromLatest ? "Last tested" : "Tested"} {formatClock(when)}
          </span>
        )}
      </div>

      <p className="m-0 font-mulish text-[14px] text-[#4c5257]">
        {result
          ? `${result.message} We connected ${path}.`
          : test.detail ?? "The test couldn't run. Try again in a few minutes."}
      </p>

      {result?.clip_url ? (
        // The clip, with the snapshot as its poster (newer admin builds).
        <CameraClip
          media={result}
          expiresAt={result.media_expires_at ?? result.snapshot_expires_at}
          onRunAgain={onRunAgain}
          canRunAgain={canRunAgain}
        />
      ) : result?.snapshot_url ? (
        <Snapshot
          url={result.snapshot_url}
          expiresAt={
            result.snapshot_expires_at ?? result.media_expires_at ?? null
          }
          onRunAgain={onRunAgain}
          canRunAgain={canRunAgain}
        />
      ) : null}

      {facts && (
        <p
          className="m-0 font-mulish text-[14px] font-bold text-actuate-blue"
          data-testid="stream-test-facts"
        >
          {facts}
        </p>
      )}

      {test.stream_url_masked && <MaskedUrl url={test.stream_url_masked} />}

      {result && !result.ok && (result.steps?.length ?? 0) > 0 && (
        <StepLadder
          camera={{
            camera_id: test.camera_id,
            steps: result.steps,
            connection: result.connection,
          }}
        />
      )}

      {finding ? (
        <div
          className="overflow-hidden rounded-md border border-[#d1d5db]"
          data-testid="stream-test-finding"
        >
          <FindingCard
            finding={finding}
            isSample={false}
            onCopy={() =>
              copyToClipboard(
                [
                  finding.title,
                  "",
                  finding.body,
                  ...finding.steps.map((step, i) => `${i + 1}. ${step}`),
                  finding.detailLine ? `\nWe saw: ${finding.detailLine}` : "",
                ]
                  .join("\n")
                  .trim()
              )
            }
          />
        </div>
      ) : result && !result.ok && result.detail ? (
        <code className="block break-words font-mono text-[12px] text-[#6b7075]">
          {result.detail}
        </code>
      ) : null}
    </div>
  );
};

export const StreamTestPanel = ({ cameraId }: { cameraId: number }) => {
  const stream = useStreamTest(cameraId);
  const status = stream.test?.status;
  const finished = stream.test && (status === "done" || status === "failed");

  if (stream.siteType) {
    return (
      <p
        className="m-0 font-mulish text-[14px] text-[#4c5257]"
        data-testid="stream-test-site-type"
      >
        {STREAM_TEST_SITE_TYPE_TEXT}: this camera sends emails or partner
        events, so there&apos;s no video stream to test. The camera check shows
        what we receive from it.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="stream-test-panel">
      <p className="m-0 font-mulish text-[14px] text-[#4c5257]">
        We open this camera&apos;s video from our side, over the same network
        path our connector uses, and show you what we see.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        {stream.off ? (
          <span
            className="font-mulish text-[14px] text-[#4c5257]"
            data-testid="stream-test-off"
          >
            {STREAM_TEST_OFF_TEXT}
          </span>
        ) : (
          <TsButton
            onClick={stream.start}
            disabled={stream.running}
            data-testid="stream-test-run"
          >
            {stream.running
              ? "Testing…"
              : finished
              ? "Run again"
              : "Test stream"}
          </TsButton>
        )}
      </div>

      {stream.notice && (
        <NoticeBox
          notice={stream.notice}
          onShowRun={stream.follow}
          onDismiss={stream.clearNotice}
        />
      )}

      {stream.loadingLatest && !stream.running && (
        <Spinner text="Looking for an earlier test…" />
      )}

      {stream.running && (
        <Spinner
          text={`${
            status === "running"
              ? "Opening the stream from our side…"
              : "Starting the test from our side… this takes about a minute."
          } ${formatElapsed(stream.elapsedMs)}`}
        />
      )}

      {stream.test && finished && (
        <ResultCard
          test={stream.test}
          fromLatest={stream.fromLatest}
          onRunAgain={stream.start}
          canRunAgain={!stream.off && !stream.running}
        />
      )}
    </div>
  );
};
